import { FACE_SIZE_PICKED, PIN_SIZE_SELECTED } from './markerStyle';

/**
 * Where people's faces sit on a map (the crew map's people layer), in SCREEN pixels: pure, so
 * a test holds it, and run by the people layer on every draw, pan, zoom and resize.
 *
 * ⭐ Screen space, not coordinates: two faces collide when they are close ON SCREEN, which
 * depends on the zoom. Two techs on one shared job collide at every zoom; a tech en route and
 * the crew at a third job collide only zoomed out. One rule covers both.
 *
 * ⚠️ Nothing here invents a position (ADR 0001). A face at a job is ringed round THAT job's
 * pin; a face en route slides ALONG its own leg, never off it.
 */

/** The gap kept between two faces side by side. */
const FACE_GAP = 4;
/** Centre to centre, two faces side by side: the larger face plus the gap. */
export const FACE_SPAN = FACE_SIZE_PICKED + FACE_GAP;
/** Slack for rounding offsets to whole pixels. */
const ROUNDING = 2;
/** How close two face centres may come. */
const FACE_MIN = FACE_SPAN - ROUNDING;
/** The gap kept between a face and the pin it rings. */
const PIN_GAP = 2;
/** Centre of a pin to centre of a face around it: clear of even a selected pin. */
export const PIN_CLEAR = PIN_SIZE_SELECTED / 2 + FACE_SIZE_PICKED / 2 + PIN_GAP;
/** How close a face at a pin may come to ANY pin's centre, in the first try. */
const PIN_MIN = PIN_CLEAR - 1;
/** Half the width of a pin's number: a face may cover a pin's rim, never this. */
const NUMBER_HALF = 6;
/**
 * Closer than this, a face overlaps a pin's disc, and would take its taps. Measured against a
 * SELECTED pin (the larger disc) whether or not it is selected: a selection never relays out
 * the faces, so the overlap must already hold for the pin at its biggest.
 */
const OVER_PIN = PIN_SIZE_SELECTED / 2 + FACE_SIZE_PICKED / 2;
/** How much wider than its plain ring a ring may grow to keep a neighbour's pin clear. */
const NEAR = 18;
/**
 * 🔴 The farthest a face at a job ever sits from ITS pin's centre: the pin's clearance plus
 * one and a half faces. Past it, a face reads as somewhere else, a position (ADR 0001). So a
 * ring never grows past it to get into view: a face whose pin is under a floating card stays
 * under the card with it, and a crowd too big for the reach overlaps rather than drift away.
 */
export const AT_JOB_REACH = PIN_CLEAR + 1.5 * FACE_SIZE_PICKED;
/** The ring search's steps: its radius, and its turn (24 turns of 15°). */
const RING_STEP = 6;
const RING_TURNS = 24;
/** How far, at a time, faces on a leg slide along it to clear one another. */
const SLIDE_STEP = 14;
/** Two pins within half a pixel are one point. */
const SAME_POINT = 2;

export type Xy = { x: number; y: number };

/** One face, where the map would put it: its point on screen. */
export interface FacePoint {
  id: string;
  x: number;
  y: number;
  /** En route: the leg's two ends on screen; the face stays on this segment. Null: at a pin. */
  leg: { from: Xy; to: Xy } | null;
  /** The words under it (a transit face's "left A at T"), if any. */
  tag: string | null;
}

/** Where a face is drawn. */
export interface FacePlace {
  /** Pixels from its own point. */
  offset: [number, number];
  /** Shared words are shown once per group, under its lowest face. */
  showTag: boolean;
  /**
   * It lies over a job pin. Still drawn ABOVE the pins (a face hidden under a neighbour's pin
   * is a tech nobody can see), but the map lets a tap pass through it to the pin.
   */
  overPin: boolean;
}

/** The part of the map a reader can see: the container less what floats over it. */
export interface FaceView {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

type Placed = { point: FacePoint; at: Xy; group: number };

const byId = (a: { id: string }, b: { id: string }) =>
  a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
const dist = (a: Xy, b: Xy) => Math.hypot(a.x - b.x, a.y - b.y);
const far = (a: Xy, b: Xy, d: number) => dist(a, b) >= d;
const inside = (s: Xy, view: FaceView | null) => {
  const half = FACE_SIZE_PICKED / 2;
  return (
    view === null ||
    (s.x - half >= view.left &&
      s.x + half <= view.right &&
      s.y - half >= view.top &&
      s.y + half <= view.bottom)
  );
};

/**
 * Where `n` faces sharing one centre sit, in pixels from it: a lone face off a pin on the
 * point itself; otherwise a ring, first at the top and clockwise, wide enough that neighbours
 * do not overlap and, round a pin, clear of it.
 */
export function ringOffsets(n: number, aroundPin: boolean): [number, number][] {
  if (n <= 0) return [];
  if (n === 1 && !aroundPin) return [[0, 0]];
  const r = ringRadius(n, aroundPin);
  return Array.from({ length: n }, (_, k) => {
    const a = -Math.PI / 2 + (2 * Math.PI * k) / n;
    // `+ 0` turns a -0 into 0, so a test can compare offsets with toEqual.
    return [Math.round(r * Math.cos(a)) + 0, Math.round(r * Math.sin(a)) + 0];
  });
}

function ringRadius(n: number, aroundPin: boolean): number {
  const apart = n > 1 ? FACE_SPAN / (2 * Math.sin(Math.PI / n)) : 0;
  return Math.max(aroundPin ? PIN_CLEAR : 0, apart);
}

/**
 * Lay faces out so none is drawn over another, en-route faces stay on their legs, faces at a
 * job stay within `AT_JOB_REACH` of its pin, and a face that must lie over a pin says so
 * (`overPin`) so the map lets taps through it to the pin.
 *
 * Faces on legs go first (they may only slide along their legs, so they get first claim), then
 * the faces at pins ring round their own pin, clear of everything placed.
 */
export function spreadFaces(
  points: readonly FacePoint[],
  pins: readonly Xy[] = [],
  view: FaceView | null = null,
): Map<string, FacePlace> {
  let group = 0;
  const next = () => group++;
  const placed = layoutLineFaces(
    points.filter((p) => p.leg !== null),
    pins,
    view,
    next,
  );
  placed.push(
    ...layoutPinFaces(
      points.filter((p) => p.leg === null),
      placed,
      pins,
      view,
      next,
    ),
  );
  return assignTags(placed, pins);
}

/** One leg's key, whichever way round it was given. */
function legKey(p: FacePoint): string {
  const { from, to } = p.leg!;
  const a = `${Math.round(from.x * SAME_POINT)},${Math.round(from.y * SAME_POINT)}`;
  const b = `${Math.round(to.x * SAME_POINT)},${Math.round(to.y * SAME_POINT)}`;
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Faces on legs: those on one leg are spaced along it round its middle, then the whole group
 * slides along the leg (0, +step, −step, +2 steps …) until it clears every face placed so far.
 * Tried first clear of the end pins and the covered area, then anywhere on the segment; when
 * the leg is too short on screen for its faces, they are spread over it and may overlap. A
 * face is NEVER moved off its segment: off the line, it would read as a position.
 */
function layoutLineFaces(
  faces: FacePoint[],
  pins: readonly Xy[],
  view: FaceView | null,
  nextGroup: () => number,
): Placed[] {
  const legs = new Map<string, FacePoint[]>();
  for (const p of [...faces].sort(byId)) {
    legs.set(legKey(p), [...(legs.get(legKey(p)) ?? []), p]);
  }
  const placed: Placed[] = [];
  for (const group of legs.values()) {
    const { from, to } = group[0]!.leg!;
    const len = dist(from, to);
    const u =
      len > 0 ? { x: (to.x - from.x) / len, y: (to.y - from.y) / len } : { x: 0, y: 0 };
    const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
    const along = (s: number): Xy => ({ x: mid.x + u.x * s, y: mid.y + u.y * s });
    const n = group.length;
    const spread = (k: number) => (k - (n - 1) / 2) * FACE_SPAN;
    const clear = (s: Xy) => placed.every((q) => far(s, q.at, FACE_MIN));
    const tries: [number, (s: Xy) => boolean][] = [
      // Clear of the end pins, faces and cards.
      [
        len / 2 - PIN_CLEAR,
        (s) => clear(s) && inside(s, view) && pins.every((pin) => far(s, pin, OVER_PIN)),
      ],
      // Anywhere on the segment, clear of faces.
      [len / 2, clear],
    ];
    let slots: Xy[] | null = null;
    for (const [reach, fits] of tries) {
      if (reach < 0) continue;
      for (let i = 0; slots === null && i * SLIDE_STEP <= reach * 2; i += 1) {
        const shift = (i % 2 === 0 ? 1 : -1) * Math.ceil(i / 2) * SLIDE_STEP;
        const s = group.map((_, k) => spread(k) + shift);
        if (s.every((x) => Math.abs(x) <= reach) && s.map(along).every(fits)) {
          slots = s.map(along);
        }
      }
      if (slots) break;
    }
    // Too short on screen for its faces: spread over the segment, overlapping if it must.
    slots ??= group.map((_, k) =>
      along(n === 1 ? 0 : (k / (n - 1) - 0.5) * Math.min(len, (n - 1) * FACE_SPAN)),
    );
    const g = nextGroup();
    group.forEach((point, k) => placed.push({ point, at: slots![k]!, group: g }));
  }
  return placed;
}

/** Faces at pins: one ring per pin, round THAT pin. */
function layoutPinFaces(
  faces: FacePoint[],
  placed: readonly Placed[],
  pins: readonly Xy[],
  view: FaceView | null,
  nextGroup: () => number,
): Placed[] {
  const atPin = new Map<string, FacePoint[]>();
  for (const p of [...faces].sort(byId)) {
    const key = `${Math.round(p.x * SAME_POINT)},${Math.round(p.y * SAME_POINT)}`;
    atPin.set(key, [...(atPin.get(key) ?? []), p]);
  }
  const out: Placed[] = [];
  for (const group of atPin.values()) {
    const slots = pinRing(group.length, group[0]!, [...placed, ...out], pins, view);
    const g = nextGroup();
    group.forEach((point, i) => out.push({ point, at: slots[i]!, group: g }));
  }
  return out;
}

/**
 * `n` places round a pin, never farther than `AT_JOB_REACH` from it, the first ring that fits,
 * trying in turn:
 * 1. close to the pin (at most `NEAR` px wider than the plain ring), clear of every face
 *    placed, of the covered area, and of every pin;
 * 2. as close, over the rim of a neighbour's pin at most, never its number;
 * 3. as close, clear of faces and pins, under the covered area if that is where the pin is:
 *    the cards are never a reason to move a face away from its job;
 * 4. out to the reach, clear of the faces alone.
 * Nothing fits within the reach (a crowd at one job, zoomed far out): the plain ring, capped at
 * the reach, its faces overlapping.
 */
function pinRing(
  n: number,
  c: Xy,
  placed: readonly Placed[],
  pins: readonly Xy[],
  view: FaceView | null,
): Xy[] {
  const base = Math.min(ringRadius(n, true), AT_JOB_REACH);
  const near = Math.min(base + NEAR, AT_JOB_REACH);
  const clearOfFaces = (s: Xy) => placed.every((p) => far(s, p.at, FACE_MIN));
  const clearOfPins = (s: Xy) => pins.every((pin) => far(s, pin, PIN_MIN));
  const clearOfNumbers = (s: Xy) =>
    pins.every((pin) => far(s, pin, FACE_SIZE_PICKED / 2 + NUMBER_HALF));
  const tries: [number, (s: Xy) => boolean][] = [
    [near, (s) => clearOfFaces(s) && inside(s, view) && clearOfPins(s)],
    [near, (s) => clearOfFaces(s) && inside(s, view) && clearOfNumbers(s)],
    [near, (s) => clearOfFaces(s) && clearOfPins(s)],
    [AT_JOB_REACH, clearOfFaces],
  ];
  const ring = (r: number, turn: number) =>
    Array.from({ length: n }, (_, k) => {
      const a = -Math.PI / 2 + (2 * Math.PI * turn) / RING_TURNS + (2 * Math.PI * k) / n;
      return { x: c.x + r * Math.cos(a), y: c.y + r * Math.sin(a) };
    });
  for (const [widest, fits] of tries) {
    for (let r = base; r <= widest; r += RING_STEP) {
      for (let turn = 0; turn < RING_TURNS; turn += 1) {
        const slots = ring(r, turn);
        if (slots.every(fits)) return slots;
      }
    }
  }
  return ring(base, 0);
}

/**
 * The answer: offsets, which face shows its group's words (the lowest that carries them), and
 * which faces lie over a pin.
 */
function assignTags(
  placed: readonly Placed[],
  pins: readonly Xy[],
): Map<string, FacePlace> {
  const out = new Map<string, FacePlace>();
  const shown = new Set<string>();
  for (const { point, at, group } of [...placed].sort(
    (a, b) => b.at.y - a.at.y || byId(a.point, b.point),
  )) {
    const key = `${group}|${point.tag}`;
    const showTag = point.tag !== null && !shown.has(key);
    if (point.tag !== null) shown.add(key);
    out.set(point.id, {
      offset: [Math.round(at.x - point.x) + 0, Math.round(at.y - point.y) + 0],
      showTag,
      overPin: pins.some((pin) => !far(at, pin, OVER_PIN)),
    });
  }
  return out;
}
